import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import BeforeAfter from "@/components/BeforeAfter";
import JsonLd, { breadcrumb } from "@/components/JsonLd";
import { ArrowLeft, MapPin, Ruler, Calendar, ArrowRight } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === null) return <PublicLayout><div className="max-w-7xl mx-auto px-5 py-20 text-sm" style={{color:'var(--text-muted)'}}>Loading…</div></PublicLayout>;
  if (p === false) return <PublicLayout><div className="max-w-7xl mx-auto px-5 py-20"><h1 className="font-display text-3xl">Project not found</h1><Link to="/projects" className="btn-ghost mt-4">Back to projects</Link></div></PublicLayout>;

  return (
    <PublicLayout>
      <JsonLd id="bc-project" data={breadcrumb([{name: "Home", url: "/"}, {name: "Projects", url: "/projects"}, {name: p.title, url: `/projects/${p.id}`}])} />
      <JsonLd id="project-detail" data={{
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: p.title,
        description: p.description,
        image: p.cover_image,
        creator: { "@id": "https://www.coastalbendcontractor.com/#business" },
        contentLocation: { "@type": "Place", name: p.location || "Coastal Bend, TX" },
      }} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-8">
        <Link to="/projects" className="inline-flex items-center gap-2 text-sm mb-8" style={{color:'var(--text-muted)'}}>
          <ArrowLeft size={14}/> All projects
        </Link>
        <div className="badge mb-4">{p.category.replace('_',' ')}</div>
        <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight max-w-4xl mb-6">{p.title}</h1>
        <div className="flex flex-wrap gap-5 text-sm" style={{color:'var(--text-muted)'}}>
          {p.location && <span className="flex items-center gap-1.5"><MapPin size={14}/> {p.location}</span>}
          {p.sqft && <span className="flex items-center gap-1.5"><Ruler size={14}/> {p.sqft.toLocaleString()} sqft</span>}
          {p.completed_year && <span className="flex items-center gap-1.5"><Calendar size={14}/> {p.completed_year}</span>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="rounded-2xl overflow-hidden aspect-[16/9]">
          <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <h2 className="font-display text-2xl lg:text-3xl font-bold mb-4">About this project</h2>
          <p className="text-base leading-relaxed" style={{color:'var(--text-muted)'}}>{p.description}</p>
        </div>
        <aside className="lg:col-span-4">
          <div className="card p-6">
            <div className="label-overline mb-3">Want similar work?</div>
            <h3 className="font-display text-xl font-bold mb-4">Tell us about your project.</h3>
            <Link to={`/contact?service=${p.category}`} className="btn-primary w-full justify-center" data-testid="project-cta-quote">Request a Quote <ArrowRight size={15}/></Link>
          </div>
        </aside>
      </section>

      {p.before_image && p.after_image && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <div className="label-overline mb-3">Before / After</div>
          <h2 className="font-display text-3xl font-bold mb-8">Drag to see the transformation</h2>
          <BeforeAfter before={p.before_image} after={p.after_image} />
        </section>
      )}

      {p.gallery && p.gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <h2 className="font-display text-3xl font-bold mb-8">Gallery</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {p.gallery.map((src, i) => (
              <div key={i} className="rounded-xl overflow-hidden aspect-[4/3]">
                <img src={src} alt={`${p.title} ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
